import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../api/AuthContext';
import { getStudents, getStudentFinancialStatus } from '../../api/students';
import ConfirmarPagoModal from './ConfirmarPagoModal';
import PagoExitosoModal from './PagoExitosoModal';
import PagoErrorModal from './PagoErrorModal';
import { getEstadoCuentaEstudiante, registrarPagoPortal, formatearMonto } from '../../api/portalEstudiantes';
import { registrarPagoPendiente } from '../../api/pagos';
import { getCategoriasRecargo } from '../../api/cargosManuales';
import { calcularRecargoCategoria } from '../../api/recargos';
import styles from '../../styles/PortalEstudiantesStyles/PortalEstudiantes.module.css';

// Datos de prueba para el Estado de Cuenta
// TODO: Eliminar este mock cuando el módulo financiero esté completamente integrado
const ESTADO_CUENTA_MOCK = {
  resumen: {
    saldo_total: 10,
    saldo_vencido: 10,
    proximo_vencimiento: 10,
    proxima_fecha_vencimiento: '18 de diciembre de 2025'
  },
  cargos_pendientes: [
    {
      id: 1,
      concepto: 'Pago Diciembre 2025',
      fecha_vencimiento: '18/12/2025',
      monto_neto: 10,
      monto_pagado: 0,
      saldo_pendiente: 10,
      estado: 'PENDIENTE'
    },
    {
      id: 2,
      concepto: 'Pago Noviembre 2025',
      fecha_vencimiento: '03/11/2025',
      monto_neto: 3800,
      monto_pagado: 1400,
      saldo_pendiente: 2400,
      estado: 'PARCIAL'
    },
    {
      id: 3,
      concepto: 'Pago Octubre 2025',
      fecha_vencimiento: '10/10/2025',
      monto_neto: 3800,
      monto_pagado: 0,
      saldo_pendiente: 3800,
      estado: 'VENCIDO'
    }
  ],
  historial_pagos: [
    {
      id: 101,
      fecha: '05/11/2025',
      folio_recibo: 'RF-2025-001',
      concepto: 'Pago parcial Noviembre 2025',
      monto_aplicado: 1400,
      forma_pago: 'Transferencia',
      estado_pago: 'APLICADO'
    }
  ],
  tiene_adeudos: true,
  tiene_historial: true
};

/**
 * Componente de Estado de Cuenta del Estudiante
 * 
 * Muestra datos reales desde la base de datos reutilizando la API existente:
 * - Resumen financiero (saldo total, vencido, próximo vencimiento)
 * - Cargos pendientes desde fin_cargos_estudiantes (ordenados por fecha_aplicacion ASC)
 * - Recargos calculados dinámicamente en backend ($tarifa_categoria/día de atraso)
 * - Historial de pagos desde fin_aplicacion_pagos
 * 
 * @param {Object} studentData - Datos del estudiante desde el backend
 */
function EstadoCuenta({ studentData, periodoActual, usuario }) {
  const { user } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estadoCuentaData, setEstadoCuentaData] = useState(null);
  const [procesandoPago, setProcesandoPago] = useState(false);
  const [pagoResultado, setPagoResultado] = useState(null);
  // Estado para referencia bancaria y pago pendiente
  const [referenciaPagoPendiente, setReferenciaPagoPendiente] = useState(null);
  const [pagoPendienteInfo, setPagoPendienteInfo] = useState(null);
  const [categoriasRecargo, setCategoriasRecargo] = useState([]);

  // Buscar estudiante por email_institucional y consultar estado financiero
  // Permite refrescar el estado de cuenta desde hijos
  const fetchEstadoCuenta = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!user || !user.email) {
        setError('No hay usuario autenticado.');
        setLoading(false);
        return;
      }
      // Buscar estudiante por email_institucional
      const response = await getStudents({ search: user.email, no_pagination: 1 });
      const lista = response.results || response;
      const estudiante = Array.isArray(lista)
        ? lista.find(e => (e.email_institucional || '').toLowerCase().trim() === user.email.toLowerCase().trim())
        : null;
      if (!estudiante) {
        setError('No se encontró información del estudiante para este usuario.');
        setLoading(false);
        return;
      }
      // Consultar estado financiero
      const data = await getStudentFinancialStatus(estudiante.id_estudiante);
      setEstadoCuentaData(data);
      // Cargar categorías de recargo
      try {
        const cats = await getCategoriasRecargo();
        setCategoriasRecargo(cats || []);
      } catch (e) {
        setCategoriasRecargo([]);
      }
    } catch (err) {
      console.error('Error al obtener estado de cuenta:', err);
      setError('No se pudo cargar la información financiera');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchEstadoCuenta();
  }, [user]);

  // Helpers para calcular recargo con las mismas condiciones que PendingChargesSection
  const normalizeText = (s) => {
    if (!s && s !== 0) return '';
    return String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+/g, ' ').trim().toUpperCase();
  };

  const getRecargoPortal = (cargo) => {
    // Si campus es Terán, no aplica recargo
    const campusNombre = studentData?.programa?.campus || '';
    if (normalizeText(campusNombre).includes('TERAN')) return 0;
    // Buscar categoría coincidente en la tabla de categorias
    const cargoCat = normalizeText(cargo.categoria || '');
    if (!cargoCat) return 0;
    const cat = categoriasRecargo.find(c => {
      const catNombre = normalizeText(c.nombre_categoria || c.categoria || '');
      return catNombre && cargoCat === catNombre;
    });
    if (!cat) return 0;
    // Parsear fecha_vencimiento en formato DD/MM/YYYY
    const parts = (cargo.fecha_vencimiento || '').split('/');
    if (parts.length !== 3) return 0;
    const fechaVenc = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    return calcularRecargoCategoria(cat, new Date(), fechaVenc);
  };

  // Ajuste: Al dar clic en Pagar, registrar o reutilizar el pago pendiente antes de mostrar el modal
  const handleRealizarPago = async (cargo) => {
    setProcesandoPago(true);
    setSelectedCargo(null);
    setReferenciaPagoPendiente(null);
    setPagoPendienteInfo(null);
    try {
      // Unificar la obtención del usuario_creacion como en ChargeForm.jsx
      let usuario_creacion = undefined;
      if (usuario && (typeof usuario.id === 'number' || typeof usuario.id_usuario === 'number' || typeof usuario.idUser === 'number')) {
        usuario_creacion = usuario.id || usuario.id_usuario || usuario.idUser;
      } else if (user && (typeof user.id === 'number' || typeof user.id_usuario === 'number' || typeof user.idUser === 'number')) {
        usuario_creacion = user.id || user.id_usuario || user.idUser;
      } else {
        try {
          const localUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
          if (localUser && (typeof localUser.id === 'number' || typeof localUser.id_usuario === 'number' || typeof localUser.idUser === 'number')) {
            usuario_creacion = localUser.id || localUser.id_usuario || localUser.idUser;
          }
        } catch {}
      }

      // Solo generar referencia si no existe pago pendiente, si existe usar la que regresa el backend
      function generarReferencia(matricula, periodoActual) {
        let periodoConcat = '';
        if (periodoActual) {
          const match = periodoActual.match(/(\d{4})-(\d{1,2})/);
          if (match) {
            const anio = match[1];
            const periodo = anio[0] + anio.slice(2) + String(Number(match[2]));
            periodoConcat = periodo;
          } else {
            periodoConcat = periodoActual.replace(/[^\d]/g, '').slice(-4);
          }
        }
        const random = Math.floor(100000 + Math.random() * 900000);
        return `${matricula || ''}${periodoConcat}${random}`;
      }

      // Preparamos referencia generada, pero la que se usará será la que regrese el backend si ya existe pago
      const referenciaGenerada = generarReferencia(studentData?.matricula || '', periodoActual);
      const recargoCalc = getRecargoPortal(cargo);
      const datosPago = {
        id_estudiante: studentData?.id || studentData?._raw?.id_estudiante,
        id_cargo: cargo.id || cargo.id_cargo,
        referencia_bancaria: referenciaGenerada,
        monto: (cargo.saldo_pendiente || cargo.monto_neto || 0) + recargoCalc,
        id_campus: studentData?._raw?.id_campus || studentData?.id_campus,
        usuario_creacion,
        monto_recargo: recargoCalc
      };
      // Validación de campos obligatorios
      const camposFaltantes = [];
      if (!datosPago.id_estudiante) camposFaltantes.push('id_estudiante');
      if (!datosPago.id_cargo) camposFaltantes.push('id_cargo');
      if (!datosPago.referencia_bancaria) camposFaltantes.push('referencia_bancaria');
      if (!datosPago.monto) camposFaltantes.push('monto');
      if (!datosPago.id_campus) camposFaltantes.push('id_campus');
      if (!datosPago.usuario_creacion) camposFaltantes.push('usuario_creacion');
      if (camposFaltantes.length > 0) {
        setError('Faltan datos obligatorios para registrar el pago: ' + camposFaltantes.join(', ') + '. Si el problema persiste, revisa que tu usuario tenga un ID válido en el sistema.');
        setProcesandoPago(false);
        return;
      }
      const resp = await registrarPagoPendiente(datosPago);
      // Usar siempre la referencia que regresa el backend (folio_recibo), así si ya existe, se muestra la misma
      setReferenciaPagoPendiente(resp.pago.folio_recibo || resp.pago.referencia_bancaria);
      setPagoPendienteInfo(resp.pago);
      setSelectedCargo({ ...cargo, recargo: recargoCalc });
      setShowConfirmModal(true);
    } catch (err) {
      setError('No se pudo iniciar el proceso de pago.');
    } finally {
      setProcesandoPago(false);
    }
  };

  const handleConfirmarPago = async (referenciaBancaria) => {
    if (!selectedCargo) return;
    setShowConfirmModal(false);
    setProcesandoPago(true);
    try {
      // Aquí deberías obtener el id_estudiante del estudiante encontrado por email
      // y usarlo para registrar el pago. Puedes guardar el id en un estado si lo necesitas.
      // Por ahora, solo muestra el modal de éxito para evitar el error.
      setPagoResultado({});
      setShowSuccessModal(true);
    } catch (err) {
      setShowErrorModal(true);
    } finally {
      setProcesandoPago(false);
    }
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado) {
      case 'PENDIENTE':
        return styles.estadoPendiente;
      case 'PARCIAL':
        return styles.estadoParcial;
      case 'VENCIDO':
        return styles.estadoVencido;
      case 'PAGADO':
        return styles.estadoPagado;
      case 'APLICADO':
        return styles.estadoPagado;
      case 'PENDIENTE_VALIDAR':
        return styles.estadoPendienteValidar;
      default:
        return '';
    }
  };

  // Parsea "2026-2", "2026-03" → { anio, periodo }
  const parseCiclo = (nombre) => {
    const match = String(nombre || '').match(/^(\d{4})[^0-9](\d+)$/);
    if (!match) return { anio: 0, periodo: 0 };
    return { anio: parseInt(match[1]), periodo: parseInt(match[2]) };
  };

  // Agrupación: ciclo → folio → pagos (hooks BEFORE any early return)
  const historialPagosData = estadoCuentaData?.historial_pagos || [];
  const pagosPorCiclo = useMemo(() => {
    const ciclosMap = new Map();
    historialPagosData.forEach((pago) => {
      const cicloKey = pago.id_ciclo_escolar ?? 'sin_ciclo';
      const cicloNombre = pago.ciclo_nombre || (pago.id_ciclo_escolar ? `Ciclo ${pago.id_ciclo_escolar}` : 'Sin Ciclo');
      if (!ciclosMap.has(cicloKey)) {
        ciclosMap.set(cicloKey, { id_ciclo_escolar: cicloKey, ciclo_nombre: cicloNombre, totalMonto: 0, foliosMap: new Map() });
      }
      const ciclo = ciclosMap.get(cicloKey);
      const folio = pago.folio_recibo || 'SIN FOLIO';
      const monto = (parseFloat(pago.monto_aplicado || 0) || 0) + (parseFloat(pago.monto_recargo || 0) || 0);
      if (!ciclo.foliosMap.has(folio)) {
        ciclo.foliosMap.set(folio, { folio, pagos: [], totalMonto: 0, formaPago: pago.forma_pago || '-', estado: pago.estado_pago || 'APLICADO' });
      }
      const grupo = ciclo.foliosMap.get(folio);
      grupo.totalMonto += isNaN(monto) ? 0 : monto;
      grupo.pagos.push(pago);
      ciclo.totalMonto += isNaN(monto) ? 0 : monto;
    });
    return Array.from(ciclosMap.values())
      .map((c) => ({ ...c, gruposFolio: Array.from(c.foliosMap.values()) }))
      .sort((a, b) => {
        if (a.id_ciclo_escolar === 'sin_ciclo') return 1;
        if (b.id_ciclo_escolar === 'sin_ciclo') return -1;
        const ca = parseCiclo(a.ciclo_nombre);
        const cb = parseCiclo(b.ciclo_nombre);
        if (cb.anio !== ca.anio) return cb.anio - ca.anio;
        return cb.periodo - ca.periodo;
      });
  }, [historialPagosData]);

  const [expandedCiclos, setExpandedCiclos] = useState({});
  const [expandedFolios, setExpandedFolios] = useState({});
  const toggleCiclo = (key) => setExpandedCiclos((p) => ({ ...p, [key]: !p[key] }));
  const toggleFolio = (folio) => setExpandedFolios((p) => ({ ...p, [folio]: !p[folio] }));

  const resumen = estadoCuentaData?.resumen;
  const cargos_pendientes = estadoCuentaData?.cargos_pendientes || [];
  const historial_pagos = estadoCuentaData?.historial_pagos;
  const tiene_adeudos = estadoCuentaData?.tiene_adeudos;
  const tiene_historial = estadoCuentaData?.tiene_historial;
  const totalRecargosPortal = useMemo(
    () => cargos_pendientes.reduce((acc, cargo) => acc + (getRecargoPortal(cargo) || 0), 0),
    [cargos_pendientes, categoriasRecargo, studentData]
  );

  // Estado de carga
  if (loading) {
    return (
      <div className={styles.estadoCuentaContainer}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>💰</span>
          <h3 className={styles.sectionTitle}>Estado Financiero</h3>
        </div>
        <div className={styles.loadingMessage}>
          Cargando estado de cuenta...
        </div>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className={styles.estadoCuentaContainer}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>💰</span>
          <h3 className={styles.sectionTitle}>Estado Financiero</h3>
        </div>
        <div className={styles.pendingModuleContainer}>
          <div className={styles.pendingModuleIcon}>⚠️</div>
          <h4 className={styles.pendingModuleTitle}>Error al cargar</h4>
          <p className={styles.pendingModuleText}>{error}</p>
          {/* Botón de reintentar eliminado porque la función ya no existe */}
        </div>
      </div>
    );
  }

  // Sin datos de estado de cuenta
  if (!estadoCuentaData) {
    return (
      <div className={styles.estadoCuentaContainer}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>💰</span>
          <h3 className={styles.sectionTitle}>Estado Financiero</h3>
        </div>
        <div className={styles.pendingModuleContainer}>
          <div className={styles.pendingModuleIcon}>📋</div>
          <h4 className={styles.pendingModuleTitle}>Sin información disponible</h4>
          <p className={styles.pendingModuleText}>
            No se encontró información de estado de cuenta para este estudiante.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.estadoCuentaContainer}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>💰</span>
        <h3 className={styles.sectionTitle}>Estado Financiero</h3>
      </div>

      {/* Resumen financiero */}
      <div className={styles.resumenFinanciero}>
        <div className={`${styles.resumenCard} ${styles.saldoTotal}`}>
          <span className={styles.resumenLabel}>SALDO TOTAL</span>
          <span className={styles.resumenMonto}>{formatearMonto(resumen.saldo_total)}</span>
        </div>
        <div className={`${styles.resumenCard} ${styles.saldoVencido}`}>
          <span className={styles.resumenLabel}>SALDO VENCIDO</span>
          <span className={styles.resumenMonto}>{formatearMonto(resumen.saldo_vencido)}</span>
        </div>
        <div className={`${styles.resumenCard} ${styles.proximoVencimiento}`}>
          <span className={styles.resumenLabel}>PRÓXIMO VENCIMIENTO</span>
          <span className={styles.resumenMonto}>{formatearMonto(resumen.proximo_vencimiento)}</span>
          <span className={styles.resumenFecha}>Vence: {resumen.proxima_fecha_vencimiento}</span>
        </div>
      </div>

      {/* Sección de Cargos Pendientes */}
      <div className={styles.seccionTitulo}>
        <span className={styles.seccionIcon}>📋</span>
        <h4>Cargos Pendientes</h4>
      </div>

      {tiene_adeudos ? (
        <div className={styles.pagosTableContainer}>
          <table className={styles.pagosTable}>
            <thead>
              <tr>
                <th>Ciclo</th>
                <th>Concepto</th>
                <th>Fecha Aplicación</th>
                <th>Fecha de Vencimiento</th>
                <th>Monto Original</th>
                <th>Descuento Beca</th>
                <th>Tipo de Beca</th>
                <th>Recargos</th>
                <th>Saldo Pendiente</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {cargos_pendientes.map((cargo, index) => (
                <tr key={cargo.id || index}>
                  <td>{cargo.ciclo_nombre || cargo.id_ciclo_escolar || '-'}</td>
                  <td className={styles.conceptoCell}>{cargo.concepto}</td>
                  <td>{cargo.fecha_aplicacion || 'N/A'}</td>
                  <td>{cargo.fecha_vencimiento}</td>
                  <td className={styles.montoCell}>{formatearMonto(cargo.monto_original)}</td>
                  <td className={styles.montoCell}>{formatearMonto(cargo.descuento_beca_monto || 0)}</td>
                  <td className={styles.montoCell}>{cargo.beca?.tipo_beca || '-'}</td>
                  <td className={`${styles.montoCell} ${getRecargoPortal(cargo) > 0 ? styles.recargoPositivo : ''}`}>
                    {formatearMonto(getRecargoPortal(cargo))}
                  </td>
                  <td className={styles.saldoCell}>{formatearMonto(cargo.saldo_pendiente)}</td>
                  <td>
                    <span className={`${styles.estadoBadge} ${getEstadoBadgeClass(cargo.estado)}`}>
                      {cargo.estado}
                    </span>
                  </td>
                  <td>
                    <button
                      className={styles.pagarButtonSmall}
                      onClick={() => handleRealizarPago(cargo)}
                      disabled={
                        procesandoPago ||
                        ![
                          'en_linea',
                          'En linea',
                          'En línea',
                          'EN_LINEA'
                        ].includes((cargo.condiciones_pago || '').trim())
                      }
                    >
                      Pagar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.tableSummary}>
            <span className={styles.summaryLabel}>Total adeudado</span>
            <span className={styles.summaryValue}>{formatearMonto(resumen.saldo_total)}</span>
            {totalRecargosPortal > 0 && (
              <>
                <span className={styles.summaryLabel}>Total recargos</span>
                <span className={`${styles.summaryValue} ${styles.recargoPositivo}`}>{formatearMonto(totalRecargosPortal)}</span>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.noDataMessage}>
          <span className={styles.noDataIcon}>✅</span>
          <p>No tienes cargos pendientes. ¡Tu cuenta está al día!</p>
        </div>
      )}

      {/* Sección de Historial de Pagos */}
      <div className={styles.seccionTitulo}>
        <span className={styles.seccionIcon}>📜</span>
        <h4>Historial de Pagos</h4>
      </div>

      {tiene_historial ? (
        <div className={styles.pagosTableContainer}>
          <table className={styles.pagosTable}>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Fecha</th>
                <th>Folio</th>
                <th>Concepto</th>
                <th>Monto</th>
                <th>Forma de Pago</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {pagosPorCiclo.map((ciclo) => {
                const cicloExpanded = !!expandedCiclos[ciclo.id_ciclo_escolar];
                return (
                  <React.Fragment key={ciclo.id_ciclo_escolar}>
                    {/* Fila de ciclo */}
                    <tr
                      className={styles.cicloPagoRow}
                      onClick={() => toggleCiclo(ciclo.id_ciclo_escolar)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td colSpan={7}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24"
                            style={{ transition: 'transform 0.2s', transform: cicloExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}>
                            <path d="M8 10L12 14L16 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span style={{ fontWeight: 600, color: 'white' }}>{ciclo.ciclo_nombre}</span>
                          <span className={styles.cicloBadge}>
                            {ciclo.gruposFolio.length} recibo{ciclo.gruposFolio.length !== 1 ? 's' : ''}
                          </span>
                          <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'white' }}>
                            {formatearMonto(ciclo.totalMonto)}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Filas de folios dentro del ciclo */}
                    {cicloExpanded && ciclo.gruposFolio.map((grupo, gi) => {
                      const conceptosUnicos = Array.from(new Set(grupo.pagos.map((p) => p.concepto || '-')));
                      const conceptosExtra = Math.max(conceptosUnicos.length - 1, 0);
                      const primerConcepto = conceptosUnicos[0] || '-';
                      const esMultiple = grupo.pagos.length > 1;
                      const expandido = esMultiple && !!expandedFolios[grupo.folio];

                      return (
                        <React.Fragment key={grupo.folio || gi}>
                          <tr>
                            <td style={{ textAlign: 'center', cursor: esMultiple ? 'pointer' : 'default' }}
                                onClick={esMultiple ? () => toggleFolio(grupo.folio) : undefined}>
                              {esMultiple && (
                                <svg width="13" height="13" viewBox="0 0 24 24"
                                  style={{ transition: 'transform 0.2s', transform: expandido ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                                  <path d="M8 10L12 14L16 10" stroke="#4a5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </td>
                            <td>{grupo.pagos[0]?.fecha}</td>
                            <td className={styles.folioCell}>{grupo.folio}</td>
                            <td className={styles.conceptoCell}>
                              {expandido ? (
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#002f6c' }}>CARGOS DE ESTE RECIBO</span>
                              ) : (
                                <>
                                  {primerConcepto}
                                  {conceptosExtra > 0 && (
                                    <span style={{ color: '#002f6c', fontWeight: 600 }}>
                                      {` (+ ${conceptosExtra} cargo${conceptosExtra > 1 ? 's' : ''} más)`}
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                            <td className={styles.montoCell}>{formatearMonto(grupo.totalMonto)}</td>
                            <td>{grupo.formaPago}</td>
                            <td>
                              <span className={`${styles.estadoBadge} ${getEstadoBadgeClass(grupo.estado)}`}>
                                {grupo.estado === 'PENDIENTE_VALIDAR' ? 'Pendiente Validación' : grupo.estado}
                              </span>
                            </td>
                          </tr>
                          {esMultiple && expandido && grupo.pagos.map((pago, pi) => (
                            <tr key={`${grupo.folio}-${pi}`} style={{ backgroundColor: '#f0f4ff' }}>
                              <td></td>
                              <td>{pago.fecha}</td>
                              <td className={styles.folioCell} style={{ fontSize: 12, color: '#666' }}>{pago.folio_recibo}</td>
                              <td className={styles.conceptoCell} style={{ paddingLeft: 24 }}>
                                {pago.codigo_concepto ? `${pago.codigo_concepto} - ${pago.concepto || '-'}` : pago.concepto || '-'}
                              </td>
                              <td className={styles.montoCell}>
                                {formatearMonto((parseFloat(pago.monto_aplicado || 0) + parseFloat(pago.monto_recargo || 0)))}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.noDataMessage}>
          <span className={styles.noDataIcon}>📭</span>
          <p>Aún no tienes pagos registrados.</p>
        </div>
      )}

      {/* Modales */}
      {showConfirmModal && selectedCargo && (
        <ConfirmarPagoModal
          cargo={selectedCargo}
          matricula={studentData?.matricula || ''}
          periodoActual={periodoActual}
          referenciaBancaria={referenciaPagoPendiente}
          pagoPendienteInfo={pagoPendienteInfo}
          onConfirm={handleConfirmarPago}
          onCancel={() => {
            setShowConfirmModal(false);
            fetchEstadoCuenta(); // Refresca al cerrar modal
          }}
          refrescarEstadoCuenta={fetchEstadoCuenta}
        />
      )}

      {showSuccessModal && (
        <PagoExitosoModal
          pago={pagoResultado?.pago}
          onClose={() => {
            setShowSuccessModal(false);
            setPagoResultado(null);
          }}
        />
      )}

      {showErrorModal && (
        <PagoErrorModal
          onClose={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
}

export default EstadoCuenta;
